'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';

// ─── Design tokens (matching site system) ─────────────────────────────────────
const CYAN = '#6FD3E0';
const ORANGE = '#FF8A3D';
const GREEN = '#4ade80';
const RED = '#f87171';
const BG_DARK = '#07111C';
const BG_CARD = '#0F2234';
const BG_SECTION = '#0E1C2B';
const TEXT_PRIMARY = '#EAF4F8';
const TEXT_SECONDARY = '#C8DDE6';
const BORDER = 'rgba(111,211,224,0.12)';

// ─── Data ─────────────────────────────────────────────────────────────────────

const QUICK_BADGES = [
  { label: 'Depth', value: '18–60m', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><line x1="4" y1="20" x2="4" y2="4"/><line x1="4" y1="4" x2="20" y2="4"/><line x1="4" y1="12" x2="14" y2="12"/></svg> },
  { label: 'Access', value: 'Boat dive', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M3 17l1.5-6H20l1.5 6"/><path d="M3 17h18"/><path d="M5 17l-1 3h16l-1-3"/><path d="M8 11V7h8v4"/><path d="M12 7V4"/><path d="M10 4h4"/></svg> },
  { label: 'Level', value: 'Advanced to Technical', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg> },
  { label: 'Visibility', value: '20–40m', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
  { label: 'Type', value: 'Reef / Shoal / Wall', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M2 12c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/><path d="M2 17c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/></svg> },
];

const WHY_DIVE = [
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M2 12c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/><path d="M2 17c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/></svg>,
    title: 'Large and diverse reef system',
    desc: 'Stretching approximately 1.3km, Jubilee Shoals offers one of the most extensive natural reef environments in Cyprus',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>,
    title: 'Pinnacles, walls and drop-offs',
    desc: 'Rocky shoals and pinnacles rise from the seabed, creating dramatic underwater topography and varied dive profiles',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>,
    title: 'Caverns, overhangs and swim-throughs',
    desc: 'Natural formations throughout the reef provide exploration opportunities for suitably trained and experienced divers',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    title: 'Suitable for deeper dive profiles',
    desc: 'The site accommodates recreational, extended recreational and technical depth ranges within a single reef system',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    title: 'Strong marine life presence',
    desc: 'Open water proximity and reef structure attract a wide range of Mediterranean and pelagic species throughout the year',
  },
];

const MARINE_LIFE = [
  { name: 'Amberjack', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Sea bream', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Groupers', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Moray eels', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Loggerhead turtles', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Green turtles', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Rays (occasional)', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Pelagic species', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
];

const HIGHLIGHTS = [
  {
    title: 'Pinnacles & Shoals',
    desc: 'Reef formations rising from the seabed',
    image: '/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg',
    alt: 'Underwater pinnacles and shoal formations at Jubilee Shoals reef system near Pissouri Cyprus',
  },
  {
    title: 'Walls & Drop-offs',
    desc: 'Steeper sections with depth variation',
    image: '/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg',
    alt: 'Diver exploring wall and drop-off sections at Jubilee Shoals Limassol Cyprus',
  },
  {
    title: 'Caverns & Overhangs',
    desc: 'Natural formations and swim-throughs',
    image: '/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg',
    alt: 'Natural cavern and overhang formations within Jubilee Shoals reef Cyprus',
  },
  {
    title: 'Elephant Rock',
    desc: 'Notable formation within the reef system',
    image: '/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg',
    alt: 'Elephant Rock notable underwater formation at Jubilee Shoals near Pissouri Cyprus',
  },
  {
    title: 'Blue Water Diving',
    desc: 'Clear water and open-water feel',
    image: '/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg',
    alt: 'Diver in clear blue water at Jubilee Shoals offshore reef site Limassol Cyprus',
  },
];

const PRACTICAL_INFO = [
  { label: 'Location', value: 'Offshore near Pissouri (Limassol region)', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg> },
  { label: 'Access', value: 'Boat dive', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M3 17l1.5-6H20l1.5 6"/><path d="M3 17h18"/><path d="M5 17l-1 3h16l-1-3"/><path d="M8 11V7h8v4"/><path d="M12 7V4"/><path d="M10 4h4"/></svg> },
  { label: 'Best Season', value: 'April – November', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
  { label: 'Water Temperature', value: '16–28°C', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg> },
  { label: 'Visibility', value: '20–40m', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
];

const SAFETY_TIPS = [
  'Monitor depth and gas consumption carefully throughout the dive',
  'Use a dive computer — depth and bottom time management is essential',
  'Be aware of currents and assess entry and exit conditions before diving',
  'Avoid overhead environments unless properly trained for cavern or cave diving',
  'Follow local dive briefings from your operator — site conditions vary',
];

const CERT_CARDS = [
  {
    level: 'Advanced',
    color: CYAN,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    desc: 'Ideal for Advanced Open Water divers exploring reef and shoal structures',
    detail: 'Advanced Open Water divers can explore the reef structures, pinnacles and shallower sections of the site. Proper navigation skills and buoyancy control are essential.',
  },
  {
    level: 'Experienced',
    color: ORANGE,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>,
    desc: 'Suitable for deeper dives and navigation between pinnacles',
    detail: 'Experienced divers with logged deep dives and strong navigation skills will be well-suited to the extended recreational depth range and complex topography.',
  },
  {
    level: 'Technical',
    color: '#a78bfa',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>,
    desc: 'Deeper sections and cavern areas require technical training',
    detail: 'Sections beyond 40m and overhead environments within the reef require appropriate technical training, equipment and gas planning.',
  },
  {
    level: 'Beginner',
    color: RED,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    desc: 'Not recommended due to depth, navigation and conditions',
    detail: 'Jubilee Shoals is not suitable for beginner or newly certified divers. The depth, exposed conditions and complex topography require significant experience.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function JubileeShoalsPage() {
  const [hoveredHighlight, setHoveredHighlight] = useState<number | null>(null);

  return (
    <>
      <Header className="mb-0" />
      <main className="pt-16 lg:pt-20" style={{ background: BG_DARK, color: TEXT_PRIMARY, fontFamily: 'var(--font-body)' }}>

        {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
        <section className="relative min-h-[85vh] flex items-end overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0">
            <Image
              src="/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg"
              alt="Diver exploring Jubilee Shoals reef system with pinnacles and blue water near Pissouri Limassol Cyprus"
              fill
              className="object-cover"
              priority
            />
            {/* Multi-stop gradient overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to top, rgba(7,17,28,0.98) 0%, rgba(7,17,28,0.75) 40%, rgba(7,17,28,0.35) 70%, rgba(7,17,28,0.15) 100%)',
              }}
            />
            {/* Subtle teal tint at bottom */}
            <div
              className="absolute bottom-0 left-0 right-0 h-64"
              style={{
                background: 'linear-gradient(to top, rgba(111,211,224,0.06) 0%, transparent 100%)',
              }}
            />
          </div>

          {/* Hero content */}
          <div className="relative z-10 w-full container mx-auto px-4 pb-16 lg:pb-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8" style={{ color: TEXT_SECONDARY }}>
              <Link href="/dive-sites" className="hover:text-cyan-300 transition-colors">Dive Sites</Link>
              <span style={{ color: BORDER }}>›</span>
              <Link href="/dive-sites/limassol" className="hover:text-cyan-300 transition-colors">Limassol</Link>
              <span style={{ color: BORDER }}>›</span>
              <span style={{ color: CYAN }}>Jubilee Shoals</span>
            </nav>

            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5 tracking-wider uppercase"
              style={{ background: 'rgba(255,138,61,0.15)', border: '1px solid rgba(255,138,61,0.35)', color: ORANGE }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M2 12c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/><path d="M2 17c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/></svg> Advanced Natural Reef System
            </div>

            {/* Title */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-5 leading-tight"
              style={{ color: TEXT_PRIMARY, textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
            >
              Jubilee Shoals Diving<br />
              <span style={{ color: CYAN }}>(Limassol / Pissouri, Cyprus)</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl max-w-2xl mb-10 leading-relaxed" style={{ color: TEXT_SECONDARY }}>
              A large natural reef system with pinnacles, walls and caverns, offering one of the most varied and advanced dive environments in Cyprus.
            </p>

            {/* Quick info badges */}
            <div className="flex flex-wrap gap-3">
              {QUICK_BADGES?.map((b) => (
                <div
                  key={b?.label}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    background: 'rgba(15,34,52,0.85)',
                    border: `1px solid ${BORDER}`,
                    backdropFilter: 'blur(12px)',
                    color: TEXT_PRIMARY,
                  }}
                >
                  <span className="text-base">{b?.icon}</span>
                  <span style={{ color: TEXT_SECONDARY }} className="text-xs">{b?.label}:</span>
                  <span style={{ color: CYAN }} className="font-semibold">{b?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 2. OVERVIEW ─────────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                About Jubilee Shoals
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <p className="text-lg leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                Jubilee Shoals is one of Cyprus&apos;s most extensive natural reef systems, stretching for approximately
                1.3km off the coast near Pissouri in the Limassol region. The site features a complex underwater
                landscape of rocky shoals, pinnacles, walls and drop-offs rising from the seabed.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                The diversity of terrain allows for a wide range of dive profiles, from advanced recreational dives
                along reef structures to deeper exploration of caverns and technical-depth sections. It is considered
                one of the most varied natural dive sites in Cyprus.
              </p>
            </div>
          </div>
        </section>

        {/* ── 3. WHY DIVE JUBILEE SHOALS ──────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-headline font-bold mb-3" style={{ color: TEXT_PRIMARY }}>
                Why Dive Jubilee Shoals?
              </h2>
              <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                Five reasons this is one of Cyprus&apos;s most rewarding advanced dive sites
              </p>
            </div>

            {/* Asymmetric bento layout — 2 large + 3 smaller */}
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
              {WHY_DIVE?.slice(0, 2)?.map((item) => (
                <div
                  key={item?.title}
                  className="md:col-span-1 rounded-2xl p-7 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  <span
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{item?.icon}</span>
                  <h3 className="text-lg font-headline font-semibold" style={{ color: TEXT_PRIMARY }}>{item?.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{item?.desc}</p>
                </div>
              ))}
              {/* Span 1 col on md */}
              <div
                className="md:col-span-1 rounded-2xl p-7 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1"
                style={{
                  background: `linear-gradient(135deg, rgba(111,211,224,0.08) 0%, ${BG_CARD} 100%)`,
                  border: `1px solid rgba(111,211,224,0.2)`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}
              >
                <span
                  className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(111,211,224,0.15)', color: CYAN }}
                >{WHY_DIVE?.[2]?.icon}</span>
                <h3 className="text-lg font-headline font-semibold" style={{ color: CYAN }}>{WHY_DIVE?.[2]?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{WHY_DIVE?.[2]?.desc}</p>
              </div>
              {WHY_DIVE?.slice(3)?.map((item) => (
                <div
                  key={item?.title}
                  className="rounded-2xl p-7 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  <span
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{item?.icon}</span>
                  <h3 className="text-lg font-headline font-semibold" style={{ color: TEXT_PRIMARY }}>{item?.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{item?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4. DIVE PROFILE ─────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                Dive Profile
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-6">
              {[
                { label: 'Typical Recreational Depth', value: '18–30m', sub: 'Standard advanced recreational range' },
                { label: 'Extended Recreational Depth', value: '30–40m', sub: 'For experienced divers' },
                { label: 'Technical Range', value: 'Up to ~60m', sub: 'Requires technical training and equipment' },
                { label: 'Dive Type', value: 'Boat dive', sub: 'No shore access — operator required' },
                { label: 'Conditions', value: 'Good visibility, exposed to open sea', sub: 'Moderate currents and changing conditions possible' },
              ]?.map((item) => (
                <div
                  key={item?.label}
                  className="rounded-2xl p-6 flex flex-col gap-2"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: CYAN }}>{item?.label}</span>
                  <span className="text-xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>{item?.value}</span>
                  {item?.sub && <span className="text-sm" style={{ color: TEXT_SECONDARY }}>{item?.sub}</span>}
                </div>
              ))}
            </div>

            {/* Conditions note */}
            <div
              className="rounded-xl px-6 py-4 flex items-start gap-3"
              style={{
                background: 'rgba(255,138,61,0.06)',
                border: '1px solid rgba(255,138,61,0.25)',
              }}
            >
              <span className="text-xl mt-0.5">⚠️</span>
              <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                <strong style={{ color: ORANGE }}>Conditions note:</strong> Jubilee Shoals is an exposed offshore site. Currents, surge and visibility can vary depending on weather and season.
              </p>
            </div>
          </div>
        </section>

        {/* ── 5. DIFFICULTY & LEVEL ───────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-headline font-bold mb-3" style={{ color: TEXT_PRIMARY }}>
                Difficulty &amp; Level
              </h2>
              <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                Jubilee Shoals is suited to advanced, experienced and technical divers
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-8">
              {CERT_CARDS?.map((card) => (
                <div
                  key={card?.level}
                  className="rounded-2xl p-7 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${card?.color}30`,
                    boxShadow: `0 4px 24px ${card?.color}10`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: `${card?.color}18`, color: card?.color }}
                    >{card?.icon}</span>
                    <h3 className="text-xl font-headline font-bold" style={{ color: card?.color }}>{card?.level}</h3>
                  </div>
                  <p className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>{card?.desc}</p>
                  <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{card?.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 6. MARINE LIFE ──────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                Marine Life
              </h2>
            </div>
            <p className="text-base mb-8 max-w-2xl" style={{ color: TEXT_SECONDARY }}>
              The reef structure and open-water proximity of Jubilee Shoals supports a strong variety of Mediterranean and pelagic species:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {MARINE_LIFE?.map((species) => (
                <div
                  key={species?.name}
                  className="flex items-center gap-3 rounded-xl px-5 py-4"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <span
                    className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{species?.icon}</span>
                  <span className="font-medium text-sm" style={{ color: TEXT_PRIMARY }}>{species?.name}</span>
                </div>
              ))}
            </div>
            <div
              className="rounded-xl px-6 py-4 flex items-start gap-3"
              style={{
                background: 'rgba(74,222,128,0.06)',
                border: '1px solid rgba(74,222,128,0.2)',
              }}
            >
              <span className="text-xl mt-0.5">🐢</span>
              <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                <strong style={{ color: GREEN }}>Turtle sightings:</strong> Loggerhead and Green turtles are occasionally encountered at Jubilee Shoals. Rays and pelagic species are also possible in open water sections.
              </p>
            </div>
          </div>
        </section>

        {/* ── 7. HIGHLIGHTS ───────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-headline font-bold mb-3" style={{ color: TEXT_PRIMARY }}>
                Dive Highlights
              </h2>
              <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                Five defining features of the Jubilee Shoals dive experience
              </p>
            </div>

            {/* Horizontal scroll on mobile, grid on desktop */}
            <div
              className="flex gap-5 overflow-x-auto pb-4 md:grid md:grid-cols-5 md:overflow-visible"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {HIGHLIGHTS?.map((card, idx) => (
                <div
                  key={card?.title}
                  className="relative flex-shrink-0 w-64 md:w-auto rounded-2xl overflow-hidden cursor-pointer"
                  style={{
                    aspectRatio: '3/4',
                    scrollSnapAlign: 'start',
                    boxShadow: hoveredHighlight === idx ? `0 12px 40px rgba(111,211,224,0.2)` : '0 4px 20px rgba(0,0,0,0.4)',
                    transition: 'box-shadow 0.3s ease',
                  }}
                  onMouseEnter={() => setHoveredHighlight(idx)}
                  onMouseLeave={() => setHoveredHighlight(null)}
                >
                  <Image
                    src={card?.image}
                    alt={card?.alt}
                    fill
                    className="object-cover"
                    style={{
                      transform: hoveredHighlight === idx ? 'scale(1.06)' : 'scale(1)',
                      transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                  />
                  {/* Gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(to top, rgba(7,17,28,0.92) 0%, rgba(7,17,28,0.3) 50%, transparent 100%)',
                    }}
                  />
                  {/* Card content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-base font-headline font-bold mb-1" style={{ color: TEXT_PRIMARY }}>{card?.title}</h3>
                    <p className="text-xs leading-relaxed" style={{ color: TEXT_SECONDARY }}>{card?.desc}</p>
                  </div>
                  {/* Number badge */}
                  <div
                    className="absolute top-4 left-4 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: 'rgba(111,211,224,0.15)', border: `1px solid ${CYAN}40`, color: CYAN }}
                  >
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 8. PRACTICAL INFO ───────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                Practical Information
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PRACTICAL_INFO?.map((item) => (
                <div
                  key={item?.label}
                  className="rounded-2xl p-6 flex items-start gap-4"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <span
                    className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{item?.icon}</span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: CYAN }}>{item?.label}</p>
                    <p className="text-sm font-medium leading-relaxed" style={{ color: TEXT_PRIMARY }}>{item?.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. SAFETY DISCLAIMER (IMPORTANT BOX) ───────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4 max-w-3xl">
            <div
              className="rounded-2xl p-8 md:p-10"
              style={{
                background: 'linear-gradient(135deg, rgba(248,113,113,0.08) 0%, rgba(15,34,52,0.9) 100%)',
                border: '2px solid rgba(248,113,113,0.35)',
                boxShadow: '0 4px 30px rgba(248,113,113,0.08)',
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl">⚠️</span>
                <h2 className="text-2xl font-headline font-bold" style={{ color: RED }}>
                  Important
                </h2>
              </div>
              <div className="space-y-4">
                <p className="text-base leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                  Jubilee Shoals includes deeper sections, complex topography and occasional overhead environments.
                  Conditions can include currents and changing visibility.
                </p>
                <p className="text-base leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                  Divers should only attempt profiles appropriate to their certification and experience level.
                </p>
                <p className="text-base leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                  Caverns and deeper areas require advanced or technical training. Always dive with a qualified guide
                  or local operator familiar with the site.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 10. SAFETY & TIPS ───────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-3xl">
            <div
              className="rounded-2xl p-8 md:p-10"
              style={{
                background: 'linear-gradient(135deg, rgba(111,211,224,0.06) 0%, rgba(15,34,52,0.8) 100%)',
                border: `1px solid rgba(111,211,224,0.18)`,
              }}
            >
              <div className="flex items-center gap-3 mb-7">
                <span className="text-2xl">🛡️</span>
                <h2 className="text-2xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                  Safety &amp; Tips
                </h2>
              </div>
              <ul className="space-y-4">
                {SAFETY_TIPS?.map((tip) => (
                  <li key={tip} className="flex items-start gap-3">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${CYAN}20`, color: CYAN, border: `1px solid ${CYAN}40` }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                    <span className="text-base leading-relaxed" style={{ color: TEXT_SECONDARY }}>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── 11. CTA ─────────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4 max-w-3xl text-center">
            {/* Hero image strip */}
            <div className="relative w-full h-48 rounded-2xl overflow-hidden mb-10">
              <Image
                src="/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg"
                alt="Diver exploring Jubilee Shoals reef and pinnacles near Pissouri Limassol Cyprus"
                fill
                className="object-cover"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(14,28,43,0.9) 0%, rgba(14,28,43,0.3) 100%)' }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <h2 className="text-3xl md:text-4xl font-headline font-bold mb-2" style={{ color: TEXT_PRIMARY }}>
                  Explore Limassol Diving
                </h2>
                <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                  Discover more Limassol dive sites or find a dive centre in the region
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/dive-sites/limassol"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: `linear-gradient(135deg, ${CYAN} 0%, #4bbfcc 100%)`,
                  color: '#07111C',
                  boxShadow: `0 4px 20px rgba(111,211,224,0.3)`,
                }}
              >
                <span>🗺️</span>
                Explore more Limassol dive sites
              </Link>
              <Link
                href="/limassol-dive-centres"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: 'transparent',
                  color: TEXT_PRIMARY,
                  border: `1px solid rgba(111,211,224,0.3)`,
                }}
              >
                <span>🏪</span>
                Find dive centres in Limassol
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
